import {
    collection,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    getDocs,
    getDoc,
    query,
    orderBy,
    serverTimestamp,
    Timestamp,
} from 'firebase/firestore';
import { db } from './config';
import { uploadBase64ImageToCloudinary } from '../services/cloudinaryService';

// Pub Package data interface
export interface PubPackage {
    id?: string;
    name: string;
    description: string;
    longDescription: string;
    version: string;
    pubDevUrl: string;
    githubUrl?: string;
    documentationUrl?: string;
    icon?: string;
    screenshots?: string[];
    features: string[];
    tags: string[];
    platforms: string[]; // web, android, ios, linux, macos, windows
    dependencies: string[];
    downloads?: number;
    likes?: number;
    pubPoints?: number; // Pub.dev quality score (0-160)
    popularity?: number;
    maintenanceScore?: number;
    dartSdkConstraint?: string;
    flutterSdkConstraint?: string;
    publishedDate: string;
    lastUpdated?: string;
    license?: string;
    author: string;
    featured?: boolean;
    order?: number;
    createdAt?: Timestamp;
    updatedAt?: Timestamp;
}

// Collection reference
const pubPackagesCollection = collection(db, 'pubPackages');

/**
 * Get all pub packages
 */
export const getAllPubPackages = async (): Promise<PubPackage[]> => {
    try {
        const pubPackagesQuery = query(pubPackagesCollection, orderBy('order', 'asc'));
        const querySnapshot = await getDocs(pubPackagesQuery);

        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        } as PubPackage));
    } catch (error) {
        console.error('Error getting pub packages:', error);
        throw error;
    }
};

/**
 * Get featured pub packages
 */
export const getFeaturedPubPackages = async (): Promise<PubPackage[]> => {
    try {
        const pubPackages = await getAllPubPackages();
        return pubPackages.filter(pkg => pkg.featured);
    } catch (error) {
        console.error('Error getting featured pub packages:', error);
        throw error;
    }
};

/**
 * Get pub package by ID
 */
export const getPubPackageById = async (id: string): Promise<PubPackage | null> => {
    try {
        const docRef = doc(pubPackagesCollection, id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            return {
                id: docSnap.id,
                ...docSnap.data()
            } as PubPackage;
        } else {
            return null;
        }
    } catch (error) {
        console.error('Error getting pub package:', error);
        throw error;
    }
};

/**
 * Create a new pub package
 */
export const createPubPackage = async (packageData: Omit<PubPackage, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    try {
        console.log('Creating pub package with data:', packageData);

        // Handle icon upload if provided as base64
        let iconUrl = packageData.icon;
        if (iconUrl && iconUrl.startsWith('data:')) {
            console.log('Uploading icon to Cloudinary...');
            try {
                iconUrl = await uploadBase64ImageToCloudinary(iconUrl, 'pub_package_icons');
                console.log('Icon uploaded successfully:', iconUrl);
            } catch (iconError) {
                console.error('Icon upload failed:', iconError);
                if (iconError instanceof Error && iconError.message.includes('environment variables')) {
                    console.warn('Skipping icon upload due to missing Cloudinary configuration');
                    iconUrl = ''; // Skip the icon if Cloudinary is not configured
                } else {
                    throw new Error(`Icon upload failed: ${iconError instanceof Error ? iconError.message : 'Unknown error'}`);
                }
            }
        }

        // Handle screenshots upload if provided as base64
        let screenshotUrls = Array.isArray(packageData.screenshots) ? packageData.screenshots : [];
        if (screenshotUrls.length > 0 && screenshotUrls.some(s => typeof s === 'string' && s.startsWith('data:'))) {
            console.log(`Uploading ${screenshotUrls.length} screenshots to Cloudinary...`);
            try {
                const uploadPromises = screenshotUrls.map(async (screenshot, index) => {
                    if (typeof screenshot === 'string' && screenshot.startsWith('data:')) {
                        console.log(`Uploading screenshot ${index + 1}...`);
                        return await uploadBase64ImageToCloudinary(screenshot, 'pub_package_screenshots');
                    }
                    return screenshot;
                });
                screenshotUrls = await Promise.all(uploadPromises);
                console.log('All screenshots uploaded successfully');
            } catch (screenshotError) {
                console.error('Screenshot upload failed:', screenshotError);
                if (screenshotError instanceof Error && screenshotError.message.includes('environment variables')) {
                    console.warn('Skipping screenshot uploads due to missing Cloudinary configuration');
                    screenshotUrls = []; // Skip screenshots if Cloudinary is not configured
                } else {
                    throw new Error(`Screenshot upload failed: ${screenshotError instanceof Error ? screenshotError.message : 'Unknown error'}`);
                }
            }
        }

        // Get current max order and add 1
        console.log('Getting current packages to determine order...');
        let packages;
        try {
            packages = await getAllPubPackages();
        } catch (fetchError) {
            console.error('Failed to fetch existing packages:', fetchError);
            throw new Error(`Failed to fetch existing packages: ${fetchError instanceof Error ? fetchError.message : 'Unknown error'}`);
        }

        const maxOrder = packages.length > 0
            ? Math.max(...packages.map(pkg => pkg.order || 0))
            : -1;

        console.log('Saving package to Firebase...');
        const docRef = await addDoc(pubPackagesCollection, {
            ...packageData,
            icon: iconUrl,
            screenshots: screenshotUrls,
            order: maxOrder + 1,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        });

        console.log('Package created successfully with ID:', docRef.id);
        return docRef.id;
    } catch (error) {
        console.error('Error creating pub package:', error);
        throw error;
    }
};

/**
 * Update a pub package
 */
export const updatePubPackage = async (id: string, packageData: Partial<Omit<PubPackage, 'id' | 'createdAt' | 'updatedAt'>>): Promise<void> => {
    try {
        // Handle icon upload if provided as base64
        let iconUrl = packageData.icon;
        if (iconUrl && iconUrl.startsWith('data:')) {
            iconUrl = await uploadBase64ImageToCloudinary(iconUrl, 'pub_package_icons');
        }

        // Handle screenshots upload if provided as base64
        let screenshotUrls = packageData.screenshots;
        if (Array.isArray(screenshotUrls) && screenshotUrls.length > 0) {
            const uploadPromises = screenshotUrls.map(async (screenshot, index) => {
                if (typeof screenshot === 'string' && screenshot.startsWith('data:')) {
                    return await uploadBase64ImageToCloudinary(screenshot, 'pub_package_screenshots');
                }
                return screenshot;
            });
            screenshotUrls = await Promise.all(uploadPromises);
        }

        const docRef = doc(pubPackagesCollection, id);
        await updateDoc(docRef, {
            ...packageData,
            ...(iconUrl && { icon: iconUrl }),
            ...(screenshotUrls && { screenshots: screenshotUrls }),
            updatedAt: serverTimestamp()
        });
    } catch (error) {
        console.error('Error updating pub package:', error);
        throw error;
    }
};

/**
 * Delete a pub package
 */
export const deletePubPackage = async (id: string): Promise<void> => {
    try {
        const docRef = doc(pubPackagesCollection, id);
        await deleteDoc(docRef);
    } catch (error) {
        console.error('Error deleting pub package:', error);
        throw error;
    }
};

/**
 * Update pub package order
 */
export const updatePubPackageOrder = async (id: string, newOrder: number): Promise<void> => {
    try {
        const docRef = doc(pubPackagesCollection, id);
        await updateDoc(docRef, {
            order: newOrder,
            updatedAt: serverTimestamp()
        });
    } catch (error) {
        console.error('Error updating pub package order:', error);
        throw error;
    }
};

/**
 * Move pub package up in order
 */
export const movePubPackageUp = async (id: string): Promise<void> => {
    try {
        const packages = await getAllPubPackages();
        const currentIndex = packages.findIndex(pkg => pkg.id === id);

        if (currentIndex > 0) {
            const currentPackage = packages[currentIndex];
            const previousPackage = packages[currentIndex - 1];

            // Swap orders
            if (currentPackage.id && previousPackage.id) {
                await updatePubPackageOrder(currentPackage.id, previousPackage.order || 0);
                await updatePubPackageOrder(previousPackage.id, currentPackage.order || 0);
            }
        }
    } catch (error) {
        console.error('Error moving pub package up:', error);
        throw error;
    }
};

/**
 * Move pub package down in order
 */
export const movePubPackageDown = async (id: string): Promise<void> => {
    try {
        const packages = await getAllPubPackages();
        const currentIndex = packages.findIndex(pkg => pkg.id === id);

        if (currentIndex < packages.length - 1) {
            const currentPackage = packages[currentIndex];
            const nextPackage = packages[currentIndex + 1];

            // Swap orders
            if (currentPackage.id && nextPackage.id) {
                await updatePubPackageOrder(currentPackage.id, nextPackage.order || 0);
                await updatePubPackageOrder(nextPackage.id, currentPackage.order || 0);
            }
        }
    } catch (error) {
        console.error('Error moving pub package down:', error);
        throw error;
    }
}; 